import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Package, Loader2, Tag } from 'lucide-react';
import {
  type Product,
  type ProductSpec,
  type Stall,
  generateId,
  formatPrice,
} from '@/lib/marketplace';

interface ProductFormProps {
  stall: Stall;
  initial?: Product;
  onSave: (product: Product) => Promise<void>;
  onCancel?: () => void;
  isSaving?: boolean;
}

export function ProductForm({ stall, initial, onSave, onCancel, isSaving }: ProductFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [price, setPrice] = useState<number>(initial?.price ?? 0);
  const [unlimited, setUnlimited] = useState(initial?.quantity === null);
  const [quantity, setQuantity] = useState<number>(initial?.quantity ?? 1);
  const [images, setImages] = useState<string[]>(initial?.images ?? ['']);
  const [categories, setCategories] = useState<string>(initial?.categories?.join(', ') ?? '');
  const [specs, setSpecs] = useState<ProductSpec[]>(
    initial?.specs?.map(([key, value]) => ({ key, value })) ?? []
  );

  // Per-product extra shipping costs (optional)
  const [shippingCosts, setShippingCosts] = useState<Record<string, number>>(
    Object.fromEntries((initial?.shipping ?? []).map(s => [s.id, s.cost]))
  );

  const addSpec = () => setSpecs(s => [...s, { key: '', value: '' }]);
  const removeSpec = (idx: number) => setSpecs(s => s.filter((_, i) => i !== idx));
  const updateSpec = (idx: number, patch: Partial<ProductSpec>) =>
    setSpecs(s => s.map((spec, i) => i === idx ? { ...spec, ...patch } : spec));

  const handleSave = async () => {
    if (!name.trim()) return;
    const catArr = categories.split(',').map(c => c.trim()).filter(Boolean);
    const product: Product = {
      id: initial?.id ?? generateId(),
      stall_id: stall.id,
      name: name.trim(),
      description: description.trim() || undefined,
      images: images.filter(Boolean).length > 0 ? images.filter(Boolean) : undefined,
      currency: stall.currency,
      price,
      quantity: unlimited ? null : quantity,
      specs: specs.filter(s => s.key).map(s => [s.key, s.value] as unknown as ProductSpec),
      shipping: stall.shipping
        .filter(z => shippingCosts[z.id] !== undefined && shippingCosts[z.id] > 0)
        .map(z => ({ id: z.id, cost: shippingCosts[z.id] })),
      categories: catArr.length > 0 ? catArr : undefined,
    };
    await onSave(product);
  };

  return (
    <div className="space-y-5">
      {/* Basic product info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            Product Details
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              Stall: {stall.name}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Product Name *</Label>
            <Input
              placeholder="e.g. Hardware Wallet"
              value={name}
              onChange={e => setName(e.target.value)}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description</Label>
            <Textarea
              placeholder="Describe the product…"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Price ({stall.currency}) *</Label>
              <Input
                type="number"
                min="0"
                step="any"
                placeholder="0"
                value={price}
                onChange={e => setPrice(parseFloat(e.target.value) || 0)}
                className="h-9"
              />
              {price > 0 && (
                <p className="text-[10px] text-muted-foreground">
                  {formatPrice(price, stall.currency)}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Quantity</Label>
              <div className="flex items-center gap-2 h-9">
                <Switch
                  checked={unlimited}
                  onCheckedChange={setUnlimited}
                  id="unlimited"
                />
                <Label htmlFor="unlimited" className="text-xs cursor-pointer">
                  {unlimited ? 'Unlimited' : 'Limited'}
                </Label>
              </div>
              {!unlimited && (
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="1"
                  value={quantity}
                  onChange={e => setQuantity(parseInt(e.target.value) || 0)}
                  className="h-8 text-xs mt-1"
                />
              )}
            </div>
          </div>

          {/* Images */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Image URLs</Label>
            {images.map((url, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  placeholder="https://example.com/image.jpg"
                  value={url}
                  onChange={e => setImages(imgs => imgs.map((im, i) => i === idx ? e.target.value : im))}
                  className="h-8 text-xs"
                />
                {images.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setImages(imgs => imgs.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => setImages(imgs => [...imgs, ''])}
            >
              <Plus className="h-3 w-3" />
              Add Image
            </Button>
          </div>

          {/* Categories */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium flex items-center gap-1">
              <Tag className="h-3 w-3" />
              Categories (comma-separated)
            </Label>
            <Input
              placeholder="e.g. hardware, security, bitcoin"
              value={categories}
              onChange={e => setCategories(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Specs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Specifications (optional)</CardTitle>
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={addSpec}>
              <Plus className="h-3 w-3" />Add Spec
            </Button>
          </div>
        </CardHeader>
        {specs.length > 0 && (
          <CardContent className="space-y-2">
            {specs.map((spec, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input
                  placeholder="Key (e.g. Color)"
                  value={spec.key}
                  onChange={e => updateSpec(idx, { key: e.target.value })}
                  className="h-8 text-xs flex-1"
                />
                <Input
                  placeholder="Value (e.g. Black)"
                  value={spec.value}
                  onChange={e => updateSpec(idx, { value: e.target.value })}
                  className="h-8 text-xs flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => removeSpec(idx)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      {/* Extra shipping costs per zone */}
      {stall.shipping.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Extra Shipping Cost Per Zone (optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Leave blank or 0 to use the stall's base cost for each zone.
            </p>
            {stall.shipping.map(zone => (
              <div key={zone.id} className="flex items-center gap-3">
                <span className="text-xs flex-1 text-muted-foreground">
                  {zone.name || zone.id} ({formatPrice(zone.cost, stall.currency)} base)
                </span>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="0"
                  value={shippingCosts[zone.id] ?? ''}
                  onChange={e => setShippingCosts(sc => ({
                    ...sc,
                    [zone.id]: parseFloat(e.target.value) || 0,
                  }))}
                  className="h-8 text-xs w-28"
                />
                <span className="text-xs text-muted-foreground w-12">{stall.currency}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Separator />

      <div className="flex items-center justify-between gap-3 pt-1">
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={!name.trim() || price < 0 || isSaving}
          size="sm"
          className="gap-2 ml-auto"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
          {initial ? 'Update Product' : 'List Product'}
        </Button>
      </div>
    </div>
  );
}
